/**
 * Queue API
 *
 * API endpoints for queue management operations
 */

import dayjs from "dayjs";
import toast from "react-hot-toast";

import { nextCargoApi } from "../../baseApi";
import type { GetQueueByCompanyAndShippingCompany, QueueByDay, QueueDriverPositions, QueueResponse } from "./queue.types";

/**
 * Queue API endpoints
 */
export const queueApi = nextCargoApi.injectEndpoints({
  endpoints: (build) => ({
    /**
     * Get queue by company and shipping company with date grouping
     */
    getQueueByCompanyAndShippingCompany: build.query<
      QueueResponse,
      {
        ids: { company: number; shippingCompany: number };
        sortOrder?: "asc" | "desc";
        dateFormat?: string;
      },
      GetQueueByCompanyAndShippingCompany[]
    >({
      query: ({ ids: { company, shippingCompany } }) => `/queue/company/${company}/shipping-company/${shippingCompany}`,
      transformResponse: (response, meta, arg) => {
        const sortOrder = arg.sortOrder || "desc"; // Padrão: mais recente primeiro
        const dateFormat = arg.dateFormat || "DD/MM"; // Padrão: DD/MM

        const responseByDays = response.reduce<QueueByDay[]>((acc, queue) => {
          if (!queue.date) return acc;

          const key = dayjs(queue.date).format(dateFormat);
          const existingEntry = acc.find((entry: QueueByDay) => entry.date === key);

          if (existingEntry) {
            existingEntry.queues.push(queue);
            existingEntry.length = existingEntry.queues.length;
          } else {
            acc.push({
              date: key,
              length: 1,
              queues: [queue]
            });
          }

          return acc;
        }, []);

        // Ordenar por data
        const grouped = responseByDays.sort((a, b) => {
          const dateA = dayjs(a.date, dateFormat);
          const dateB = dayjs(b.date, dateFormat);

          return sortOrder === "desc"
            ? dateB.diff(dateA) // Mais recente primeiro
            : dateA.diff(dateB); // Mais antigo primeiro
        });

        return {
          original: response,
          grouped
        };
      }
    }),

    /**
     * Get driver positions in queue
     */
    getQueueDriverPositions: build.query<QueueDriverPositions[], { ids: number[] }>({
      query: (params) => ({
        url: "/queue/driver/positions",
        params
      })
    }),

    /**
     * Reset queue for a company
     */
    resetQueue: build.mutation<void, { company: { id: number } }>({
      query: (body) => ({
        url: `/queue/reset`,
        method: "POST",
        body,
        responseHandler: "text"
      })
    }),

    /**
     * Create delay in queue
     */
    createDelayQueue: build.mutation<void, { time: string; companyId: number; delayId: number }>({
      query: (body) => ({
        url: `/queue/delay`,
        method: "PUT",
        body
      })
    })
  })
});

// Enhance endpoints with tag types
queueApi.enhanceEndpoints({
  addTagTypes: ["Queue", "QueueByShippingCompany", "QueueByCompany", "QueueDriverPositions"],
  endpoints: {
    getQueueByCompanyAndShippingCompany: {
      providesTags: (result, error, { ids }) => {
        if (!result) {
          return [
            { type: "Queue", id: "LIST" },
            { type: "QueueByCompany", id: ids.company },
            { type: "QueueByShippingCompany", id: ids.shippingCompany }
          ];
        }

        return [
          ...result.original.map((queue) => ({
            type: "QueueByShippingCompany" as const,
            id: queue.id
          })),
          { type: "Queue", id: "LIST" }
        ];
      }
    },
    getQueueDriverPositions: {
      providesTags: (result, error, { ids }) => {
        if (!result) return [{ type: "QueueDriverPositions", id: "LIST" }];

        return [
          ...ids.map((id) => ({
            type: "QueueDriverPositions" as const,
            id: id
          }))
        ];
      }
    },
    resetQueue: {
      invalidatesTags: (result, error, arg) => [{ type: "Queue", id: arg.company.id }],
      onQueryStarted: (args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Reiniciando fila...",
          success: "Fila reiniciada com sucesso!",
          error: "Erro ao reiniciar fila."
        });
      }
    },
    createDelayQueue: {
      invalidatesTags: (result, error, arg) => [{ type: "Queue", id: arg.companyId }],
      onQueryStarted: (args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Adicionando atraso na fila...",
          success: "Atraso adicionado com sucesso!",
          error: "Erro ao adicionar atraso na fila."
        });
      }
    }
  }
});
